import { COMMON_TEXT } from '@/helpers/common-text';
import {
  QUERY_STRING_STORE,
} from '@/helpers/constants';
import {
  generateRandomString,
  getConfigurationUrlWithParam,
} from '@/helpers/utility';
import { cloneDeep } from 'lodash';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import UserInfoDialog from './UserInfoDialog';
import { createAuthenticatedApiClient } from 'src/helpers/api-client';
import { UsersApi } from '@/openapi/apis/UsersApi';
import Toaster from 'src/components/CustomComponent/Toaster';
import SaveButton from 'src/components/CustomComponent/SaveButton/SaveButton';
import EditIcon from 'src/components/CustomComponent/EditIcon/EditIcon';
import DeleteIcon from 'src/components/CustomComponent/DeleteIcon/DeleteIcon';
import LoadingSpinner from 'src/components/CustomComponent/LoadingSpinner/LoadingSpinner';
import { USER_PRIVILEGE } from '@/helpers/constants/users';
import { refreshCurrentUser } from '@/helpers/auth';

@connect(state => ({
  tenantId: state.querystring?.['select-tenant']?.[0],
  user: state.memory.user,
  querystring: state.querystring,
  sessionStore: state.session,
}))
class UserSetting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tenant: this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0],
      isLoading: false,
      dataList: [],
      dialogVisibleCount: 0,
      editingUser: {},
      searchFilters: {
        userName: '',
        userEmail: '',
        userPrivilege: '',
      },
    };
    this.toastRef = React.createRef();
    this.filters = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      username: { value: null, matchMode: FilterMatchMode.CONTAINS },
      privilege: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };
    this.userRowStatus = {
      UNCHANGED: 0,
      NEW: 1,
      MODIFIED: 2,
      DELETED: 3,
    };
    this.userRowPrivilege = {
      ADMIN: 'admin',
      STANDARD: 'standard',
      SIGNAGE: 'signage',
      SUPERVISOR: 'supervisor',
    };
  }

  componentDidMount() {
    const { tenant } = this.state;
    if (tenant) {
      this.setState({ tenant }, () => {
        this.getData();
      });
    }
  }

  // Check if current user has privileges to manage users
  hasUserManagementPrivileges = () => {
    const userPrivilege = this.props.user?.privilege;

    return userPrivilege === USER_PRIVILEGE.ADMIN ||
      userPrivilege === USER_PRIVILEGE.SUPERVISOR;
  };

  componentDidUpdate(prevProps) {
    const { querystring } = this.props;
    const { querystring: prevQuerystring } = prevProps;
    const tenant = querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const prevTenant = prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const isTenantChanged = tenant !== prevTenant;
    if (isTenantChanged) {
      this.setState({ tenant }, () => {
        this.getData();
      });
    }
  }

  goBackToConfigPage = () => {
    const { querystring } = this.props;
    window.location.href = getConfigurationUrlWithParam({ querystring });
  };

  renderHeaderActions() {
    const { isLoading, isUpdating } = this.state;

    const getUpdatePayload = ({ userList }) => {
      const payload = {
        values: [],
      };

      const newUsers = userList.filter(user => user.status === this.userRowStatus.NEW);
      const modifiedUsers = userList.filter(user => user.status === this.userRowStatus.MODIFIED);
      const deletedUsers = userList.filter(user => user.status === this.userRowStatus.DELETED);

      if (newUsers.length > 0) {
        payload.values.push(...newUsers);
      }
      if (modifiedUsers.length > 0) {
        payload.values.push(...modifiedUsers);
      }
      if (deletedUsers.length > 0) {
        payload.values.push(...deletedUsers);
      }

      return { payload };
    };

    const submitButtonOnClicked = () => {
      const { dataList } = this.state;
      const userList = dataList.filter(user => user.status !== this.userRowStatus.UNCHANGED);
      if (userList.length > 0) {
        const { payload } = getUpdatePayload({ userList });
        this.updateData({ updatePayload: payload });
      } else {
        this.toastRef.current.show({
          severity: 'info',
          summary: '情報',
          detail: COMMON_TEXT.NO_CHANGE_TO_BE_SAVED,
          life: 3000,
          closable: true,
        });
      }
    };
    // Remove Add button from header actions
    return (
      <div className="header-actions">
        <SaveButton
          loading={isUpdating}
          disabled={isLoading}
          onClick={submitButtonOnClicked}
          label="保存"
        />
      </div>
    );
  }

  renderSearchSection() {
    const { searchFilters } = this.state;
    const handleSearchChange = (field, value) => {
      this.setState({
        searchFilters: {
          ...searchFilters,
          [field]: value,
        },
      });
    };
    const SearchIcon = () => (
      <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.3333 15.5L9.08333 10.25C8.66667 10.5833 8.1875 10.8472 7.64583 11.0417C7.10417 11.2361 6.52778 11.3333 5.91667 11.3333C4.40278 11.3333 3.12153 10.809 2.07292 9.76042C1.02431 8.71181 0.5 7.43056 0.5 5.91667C0.5 4.40278 1.02431 3.12153 2.07292 2.07292C3.12153 1.02431 4.40278 0.5 5.91667 0.5C7.43056 0.5 8.71181 1.02431 9.76042 2.07292C10.809 3.12153 11.3333 4.40278 11.3333 5.91667C11.3333 6.52778 11.2361 7.10417 11.0417 7.64583C10.8472 8.1875 10.5833 8.66667 10.25 9.08333L15.5 14.3333L14.3333 15.5ZM5.91667 9.66667C6.95833 9.66667 7.84375 9.30208 8.57292 8.57292C9.30208 7.84375 9.66667 6.95833 9.66667 5.91667C9.66667 4.875 9.30208 3.98958 8.57292 3.26042C7.84375 2.53125 6.95833 2.16667 5.91667 2.16667C4.875 2.16667 3.98958 2.53125 3.26042 3.26042C2.53125 3.98958 2.16667 4.875 2.16667 5.91667C2.16667 6.95833 2.53125 7.84375 3.26042 8.57292C3.98958 9.30208 4.875 9.66667 5.91667 9.66667Z" fill="white" />
      </svg>
    );
    return (
      <div className="search-inputs-container">
        <div className="search-input-wrapper">
          <SearchIcon />
          <input
            type="text"
            placeholder="名前"
            value={searchFilters.userName}
            onChange={e => handleSearchChange('userName', e.target.value)}
            className="search-input"
          />
        </div>
        <div className="search-input-wrapper">
          <SearchIcon />
          <input
            type="text"
            placeholder="メールアドレス"
            value={searchFilters.userEmail}
            onChange={e => handleSearchChange('userEmail', e.target.value)}
            className="search-input"
          />
        </div>
        <div className="search-input-wrapper">
          <SearchIcon />
          <input
            type="text"
            placeholder="権限"
            value={searchFilters.userPrivilege}
            onChange={e => handleSearchChange('userPrivilege', e.target.value)}
            className="search-input"
          />
        </div>
      </div>
    );
  }

  getData = async () => {
    // Check privileges before making API call
    if (!this.hasUserManagementPrivileges()) {
      this.setState({
        dataList: [],
        isLoading: false,
      });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const usersApi = createAuthenticatedApiClient(UsersApi);
      const response = await usersApi.listUsers({ page: 0, tenantId: this.props.tenantId });
      const users = response.users || [];
      const newUserList = users.map(user => {
        // Parse login_methods string to array
        let loginMethodsArray = [];
        if (user.loginMethods) {
          loginMethodsArray = user.loginMethods.split(',').map(m => m.trim());
        }

        return {
          username: user.userName,
          name: (user.lastname ? user.lastname + ' ' : '') + user.firstname ?? '',
          privilege: user.privilege,
          isOverrideLoginMethod: user.isOverrideLoginMethod || false,
          loginMethods: loginMethodsArray,
          status: this.userRowStatus.UNCHANGED,
          rowIndexId: generateRandomString(16),
        };
      });

      this.setState({
        dataList: cloneDeep(newUserList),
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      this.setState({
        dataList: [],
        isLoading: false,
      });
      Toaster.error(COMMON_TEXT.LOAD_DATA_FAILED || 'Failed to load user data');
    }
  };

  updateData = async ({ updatePayload }) => {
    // Additional privilege check for safety
    if (!this.hasUserManagementPrivileges()) {
      Toaster.error('この操作を実行する権限がありません。');
      return;
    }

    this.setState({ isUpdating: true });
    try {
      const usersApi = createAuthenticatedApiClient(UsersApi);

      // Handle new users
      const newUsers = updatePayload.values.filter(user => user.status === this.userRowStatus.NEW);
      for (const user of newUsers) {
        const loginMethods = Array.isArray(user.loginMethods)
          ? user.loginMethods.join(',')
          : user.loginMethods;

        user.loginMethods = loginMethods;

        await usersApi.createUser({ apiPostUserRequest: user });
      }

      // Handle modified users
      const modifiedUsers = updatePayload.values.filter(user => user.status === this.userRowStatus.MODIFIED);
      for (const user of modifiedUsers) {
        const loginMethods = Array.isArray(user.loginMethods)
          ? user.loginMethods.join(',')
          : user.loginMethods;

        user.loginMethods = loginMethods;

        await usersApi.updateUser({
          email: user.username,
          apiPatchUserRequest: user
        });
      }

      // Handle deleted users
      const deletedUsers = updatePayload.values.filter(user => user.status === this.userRowStatus.DELETED);
      for (const user of deletedUsers) {
        await usersApi.deleteUser({ email: user.username });
      }

      this.setState({ isUpdating: false }, () => {
        Toaster.success(COMMON_TEXT.SAVED_SUCCESSFULLY);
        this.getData();
      });

      if (newUsers.find(user => user.username === this.props.user.email)
        || modifiedUsers.find(user => user.username === this.props.user.email)
        || deletedUsers.find(user => user.username === this.props.user.email)) {
        await refreshCurrentUser();
      }
    } catch (error) {
      const message = (await error?.response?.json())?.message;
      // Check for duplicate email error
      if (
        (message && message.includes('email already signed up'))
      ) {
        this.setState({ isUpdating: false }, () => {
          Toaster.error(COMMON_TEXT.EMAIL_ALREADY_EXISTS);
        });
      } else {
        this.setState({ isUpdating: false }, () => {
          Toaster.error(COMMON_TEXT.SAVE_FAILED);
        });
      }
    }
  };

  customSortIcon = (options) => {
    let iconSrc;
    if (options.sorted) {
      if (options.sortOrder < 0) {
        iconSrc = "/assets/img/sort-down-icon.svg";
      } else {
        iconSrc = "/assets/img/sort-up-icon.svg";
      }
    } else {
      iconSrc = "/assets/img/sort-icon.svg";
    }
    return (
      <img
        src={iconSrc}
        alt="sort"
        className="custom-sort-icon"
        {...options.iconProps}
      />
    );
  };

  renderUserTable() {
    const { dataList, searchFilters } = this.state;

    // Filter data based on search filters
    const filteredData = dataList.filter(user => {
      const nameMatch = !searchFilters.userName ||
        (user.name && user.name.toLowerCase().includes(searchFilters.userName.toLowerCase()));
      const emailMatch = !searchFilters.userEmail ||
        (user.username && user.username.toLowerCase().includes(searchFilters.userEmail.toLowerCase()));
      const privilegeMatch = !searchFilters.userPrivilege ||
        (user.privilege && user.privilege.toLowerCase().includes(searchFilters.userPrivilege.toLowerCase()));
      return nameMatch && emailMatch && privilegeMatch;
    });

    const confirmDeleteUser = ({ index, userList }) => {
      let user = userList[index];
      const { username } = user;
      if (username && user.status !== this.userRowStatus.NEW) {
        user.status = this.userRowStatus.DELETED;
        userList[index] = user;
      } else {
        userList.splice(index, 1);
      }

      this.setState({ dataList: userList });
    };

    const deleteUserButtonOnClicked = async (
      e,
      { user, index, userList }
    ) => {
      const { name, username, privilege } = user;
      confirmDialog({
        message: () => (
          <div
            className="user-info-container delete-message-container"
            style={{ padding: '0px 40px 0px 20px', maxHeight: 'calc(70vh - 140px)', overflowY: 'auto', background: 'none' }}
          >
            <div className="flex align-items-center" style={{ marginTop: 8 }}>
              <div style={{ width: '120px', color: '#A2A8BB', fontWeight: 700 }}>{COMMON_TEXT.NAME}</div>
              <div>
                <b style={{ color: '#fff' }}>{name}</b>
              </div>
            </div>
            <div className="mt-2 flex align-items-center" style={{ marginBottom: 8 }}>
              <div style={{ width: '120px', color: '#A2A8BB', fontWeight: 700 }}>{COMMON_TEXT.EMPLOYEE_EMAIL}</div>
              <div>
                <b style={{ color: '#fff' }}>{username}</b>
              </div>
            </div>
            <div className="mt-2 flex align-items-center">
              <div style={{ width: '120px', color: '#A2A8BB', fontWeight: 700 }}>{COMMON_TEXT.PRIVILEGE}</div>
              <div>
                <b style={{ color: '#fff' }}>{privilege}</b>
              </div>
            </div>
          </div>
        ),
        header: '削除してよろしいですか？',
        icon: null,
        className: 'user-setting-info-dialog',
        style: { width: '500px' },
        accept: () => confirmDeleteUser({ index, userList }),
        reject: () => { },
        acceptClassName: 'p-button-danger',
        rejectClassName: 'p-button-secondary p-button-text',
        appendTo: 'self',
      });
    };

    const editUserButtonOnClicked = (e, { user, userIndex }) => {
      this.setState({
        editingUser: { ...user },
        dialogVisibleCount: new Date().getTime(),
      });
    };

    const getRowIndex = rowData => {
      return dataList.findIndex(data => {
        return data.rowIndexId === rowData.rowIndexId;
      });
    };

    // Helper function to check if deletion should be disabled
    const isDeleteDisabled = (rowData) => {
      const { status, username: targetUsername } = rowData; // Target user's username and status

      // Already deleted users can't be deleted again
      if (status === this.userRowStatus.DELETED) {
        return true;
      }

      // Prevent current user from deleting themselves
      if (this.props.user.email &&
        targetUsername === this.props.user.email) {
        return true;
      }

      return false;
    };

    const actionBodyTemplate = rowData => {
      const rowIndex = getRowIndex(rowData);
      const { status } = rowData;
      const deleteDisabled = isDeleteDisabled(rowData);
      return (
        <div className="action-body-container" style={{ display: 'flex', gap: '10px' }}>
          <Button
            type="button"
            text
            disabled={status === this.userRowStatus.DELETED}
            severity="info"
            onClick={e =>
              editUserButtonOnClicked(e, {
                user: rowData,
                userIndex: rowIndex,
              })
            }
          >
            <EditIcon />
          </Button>
          <Button
            type="button"
            text
            disabled={deleteDisabled}
            severity="danger"
            onClick={e =>
              deleteUserButtonOnClicked(e, {
                user: rowData,
                index: rowIndex,
                userList: dataList,
              })
            }
          >
            <DeleteIcon />
          </Button>
        </div>
      );
    };

    const privilegeBodyTemplate = (rowData, rowProps) => {
      const { privilege } = rowData;
      let privilegeText = '';
      if (privilege === this.userRowPrivilege.ADMIN) {
        privilegeText = '管理者';
      } else if (privilege === this.userRowPrivilege.STANDARD) {
        privilegeText = '標準';
      } else if (privilege === this.userRowPrivilege.SIGNAGE) {
        privilegeText = 'サイネージ';
      } else if (privilege === this.userRowPrivilege.SUPERVISOR) {
        privilegeText = '監視者';
      }
      return (<>{privilegeText}</>);
    };

    const statusBodyTemplate = (rowData, rowProps) => {
      const { status } = rowData;
      let statusText = '';
      const getSeverity = status => {
        switch (status) {
          case this.userRowStatus.NEW:
            return 'success';
          case this.userRowStatus.MODIFIED:
            return 'info';
          case this.userRowStatus.UNCHANGED:
            return '';
          case this.userRowStatus.DELETED:
            return 'danger';
          default:
            return '';
        }
      };
      if (status === this.userRowStatus.NEW) {
        statusText = '新規';
      } else if (status === this.userRowStatus.MODIFIED) {
        statusText = '修正';
      } else if (status === this.userRowStatus.UNCHANGED) {
        statusText = '';
      } else if (status === this.userRowStatus.DELETED) {
        statusText = '削除';
      }
      return (
        <div className="status-body-container">
          {statusText && (
            <Tag value={statusText} severity={getSeverity(status)} />
          )}
        </div>
      );
    };

    return (
      <>
        <div className="table-container">
          <DataTable
            className="employee-data-table"
            value={filteredData}
            dataKey="rowIndexId"
            tableStyle={{ minWidth: '60rem' }}
            scrollable
            scrollHeight="100%"
            size="medium"
            stripedRows
            rowHover
            showGridlines
            selectionMode="single"
            sortIcon={this.customSortIcon}
            headerStyle={{
              color: '#A2A8BB',
              fontWeight: 'bold',
              fontSize: 12,
              fontFamily: 'DM Sans, Noto Sans JP, sans-serif',
            }}
            bodyStyle={{
              color: '#fff',
              fontWeight: 'bold',
              fontSize: 12,
              fontFamily: 'DM Sans, Noto Sans JP, sans-serif',
            }}
            emptyMessage={() => {
              return (
                <div className="no-data-container">
                  {COMMON_TEXT.NO_DATA}
                </div>
              );
            }}
          >
            <Column field="name" header={COMMON_TEXT.NAME} sortable />
            <Column field="username" header={COMMON_TEXT.EMPLOYEE_EMAIL} sortable />
            <Column field="privilege" header={COMMON_TEXT.PRIVILEGE} sortable body={privilegeBodyTemplate} />
            <Column field="status" header={COMMON_TEXT.STATUS} body={statusBodyTemplate} />
            <Column body={actionBodyTemplate} style={{ width: '8rem' }} />
          </DataTable>
        </div>
      </>
    );
  }

  userInfoOnModified = ({ user }) => {
    user.name = (user.lastname ? user.lastname + ' ' : '') + user.firstname ?? '';
    const { dataList } = this.state;
    const { rowIndexId } = user;
    const findIndex = dataList.findIndex(
      data => data.rowIndexId === rowIndexId
    );
    if (findIndex >= 0) {
      dataList[findIndex] = {
        ...user,
      };
    } else {
      dataList.push(user);
    }
    this.setState({ dataList });
  };

  render() {
    const { dialogVisibleCount, editingUser, tenant, isLoading, dataList } = this.state;

    // AddNewUser button handler
    const addNewUserButtonOnClicked = () => {
      this.setState({
        editingUser: {
          tenantId: this.state.tenant,
          username: '',
          password: '',
          firstname: '',
          lastname: '',
          privilege: this.userRowPrivilege.STANDARD,
          status: this.userRowStatus.NEW,
          rowIndexId: generateRandomString(16),
        },
        dialogVisibleCount: new Date().getTime(),
      });
    };

    // Calculate user count (excluding deleted users)
    const userCount = dataList?.filter(user => user.status !== this.userRowStatus.DELETED).length || 0;

    return (
      <>
        <Toast ref={this.toastRef} position="top-center" />
        <ConfirmDialog />
        <UserInfoDialog
          tenantId={tenant}
          dialogVisibleCount={dialogVisibleCount}
          selectedUser={editingUser}
          updateUserInfo={this.userInfoOnModified}
          userList={this.state.dataList}
        ></UserInfoDialog>
        <div className="config-container custom-config-container user-setting-container custom-setting-config-container">
          <div className="config-title-container">
            <div className="text-left">
              <div className="title-text">
                {COMMON_TEXT.USER_SETTING}
                <span className="title-count">{userCount}</span>
              </div>
            </div>
            <div className="grid grid-nogutter align-items-center justify-content-end">
              {this.renderHeaderActions()}
            </div>
          </div>
          <div className="config-content">
            {this.renderSearchSection()}
            <div className="setting-content" style={{ position: 'relative', minHeight: '200px' }}>
              {isLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  background: 'rgba(37,47,85,0.7)'
                }}>
                  <LoadingSpinner />
                </div>
              )}
              {!isLoading && this.renderUserTable()}
              <div className="button-container">
                <Button
                  type="button"
                  label="追加"
                  severity="success"
                  className="has-shadow"
                  onClick={addNewUserButtonOnClicked}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

UserSetting.propTypes = {};

export default UserSetting; 